import { useState, useMemo, useCallback } from 'react';
import {
  addMonths,
  subMonths,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  format,
  differenceInWeeks,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, WifiOff } from 'lucide-react';
import { useProfile } from '../hooks/useProfile';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import {
  useAvailability,
  usePTO,
  useCreateAvailability,
  useDeleteAvailability,
  useCreatePTO,
  useDeletePTO,
  useCreateDateAvailability,
  useDeleteDateAvailability,
} from '../hooks/useAvailability';
import { StandardWeekEditor } from '../components/StandardWeekEditor';
import { MonthCalendarGrid } from '../components/MonthCalendarGrid';
import { DayEditorSheet } from '../components/DayEditorSheet';

const STANDARD_HOURS = { start: '08:00', end: '17:00' };

export function SchedulePage() {
  const isOnline = useOnlineStatus();
  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const personId = profile?.id;

  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Data hooks
  const { data: availabilityData, isLoading: isLoadingAvailability } = useAvailability(personId);
  const { data: pto = [], isLoading: isLoadingPTO } = usePTO(personId);

  const recurring = availabilityData?.recurring ?? [];
  const dateSpecific = availabilityData?.dateSpecific ?? [];

  // Mutations
  const createAvailabilityMutation = useCreateAvailability();
  const deleteAvailabilityMutation = useDeleteAvailability(personId ?? '');
  const createPTOMutation = useCreatePTO();
  const deletePTOMutation = useDeletePTO(personId ?? '');
  const createDateAvailabilityMutation = useCreateDateAvailability();
  const deleteDateAvailabilityMutation = useDeleteDateAvailability(personId ?? '');

  const isLoading = isLoadingProfile || isLoadingAvailability || isLoadingPTO;
  const isSaving =
    createAvailabilityMutation.isPending ||
    deleteAvailabilityMutation.isPending ||
    createPTOMutation.isPending ||
    deletePTOMutation.isPending ||
    createDateAvailabilityMutation.isPending ||
    deleteDateAvailabilityMutation.isPending;

  // Month grid calculations
  const { gridStartDate, weeksToShow } = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart);
    const gridEnd = endOfWeek(monthEnd);
    const weeks = differenceInWeeks(gridEnd, gridStart) + 1;
    return { gridStartDate: gridStart, weeksToShow: weeks };
  }, [currentMonth]);

  // --- Standard Week handlers ---

  const handleSaveStandardDay = (dayOfWeek: number, startTime: string, endTime: string) => {
    if (!personId) return;
    const existingRule = recurring.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;

    if (existingRule) {
      deleteAvailabilityMutation.mutate(existingRule.id, {
        onSuccess: () => {
          createAvailabilityMutation.mutate({
            personId,
            dayOfWeek,
            startTime,
            endTime,
            effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
          });
        },
      });
    } else {
      createAvailabilityMutation.mutate({
        personId,
        dayOfWeek,
        startTime,
        endTime,
        effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
      });
    }
  };

  const handleDeleteStandardDay = (dayOfWeek: number) => {
    if (!personId) return;
    const existingRule = recurring.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
    if (existingRule) {
      deleteAvailabilityMutation.mutate(existingRule.id);
    }
  };

  const handleSetStandardHours = () => {
    if (!personId) return;
    for (let day = 1; day <= 5; day++) {
      const existingRule = recurring.find((r) => r.dayOfWeek === day);
      if (!existingRule) {
        createAvailabilityMutation.mutate({
          personId,
          dayOfWeek: day,
          startTime: STANDARD_HOURS.start,
          endTime: STANDARD_HOURS.end,
          effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
        });
      }
    }
  };

  // --- Calendar day selection ---

  const handleSelectDate = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  // Selected date data
  const selectedDateData = useMemo(() => {
    if (!selectedDate) return null;
    const dayOfWeek = selectedDate.getDay();
    const standardRule = recurring.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const override = dateSpecific.find((d) => d.date === dateStr) ?? null;
    const ptoDay = pto.find((p) => p.date === dateStr) ?? null;
    return { standardRule, override, ptoDay };
  }, [selectedDate, recurring, dateSpecific, pto]);

  // --- Day editor sheet handlers ---

  const handleSaveCustomHours = (data: { startTime: string; endTime: string }) => {
    if (!selectedDate || !personId) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingOverride = dateSpecific.find((d) => d.date === dateStr);

    const onDone = () => setSelectedDate(null);

    if (existingOverride) {
      deleteDateAvailabilityMutation.mutate(dateStr, {
        onSuccess: () => {
          createDateAvailabilityMutation.mutate(
            { personId, date: dateStr, startTime: data.startTime, endTime: data.endTime },
            { onSettled: onDone }
          );
        },
        onError: onDone,
      });
    } else {
      createDateAvailabilityMutation.mutate(
        { personId, date: dateStr, startTime: data.startTime, endTime: data.endTime },
        { onSettled: onDone }
      );
    }
  };

  const handleClearOverride = () => {
    if (!selectedDate || !personId) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingOverride = dateSpecific.find((d) => d.date === dateStr);
    if (existingOverride) {
      deleteDateAvailabilityMutation.mutate(dateStr, {
        onSettled: () => setSelectedDate(null),
      });
    } else {
      setSelectedDate(null);
    }
  };

  const handleSetPto = () => {
    if (!selectedDate || !personId) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingOverride = dateSpecific.find((d) => d.date === dateStr);

    const doCreatePto = () => {
      createPTOMutation.mutate(
        { personId, date: dateStr },
        { onSettled: () => setSelectedDate(null) }
      );
    };

    // Clear any existing date override before setting PTO
    if (existingOverride) {
      deleteDateAvailabilityMutation.mutate(dateStr, {
        onSuccess: doCreatePto,
        onError: () => setSelectedDate(null),
      });
    } else {
      doCreatePto();
    }
  };

  const handleClearPto = () => {
    if (!selectedDate || !personId) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingPto = pto.find((p) => p.date === dateStr);
    if (existingPto) {
      deletePTOMutation.mutate(existingPto.id, {
        onSettled: () => setSelectedDate(null),
      });
    } else {
      setSelectedDate(null);
    }
  };

  // --- Offline state ---
  if (!isOnline) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-gray-500 px-4">
        <WifiOff className="h-12 w-12 mb-3 text-gray-400" />
        <p className="text-lg font-medium text-gray-700">Schedule unavailable offline</p>
        <p className="text-sm text-center mt-1">
          Connect to the internet to view and edit your schedule.
        </p>
      </div>
    );
  }

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Header with month navigation */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentMonth((prev) => subMonths(prev, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentMonth((prev) => addMonths(prev, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={() => setCurrentMonth(startOfMonth(new Date()))}
              className="px-2 py-1 text-sm text-gray-500 hover:text-gray-900"
            >
              Today
            </button>
          </div>
          <span className="text-sm font-semibold text-gray-900">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
        </div>
      </div>

      {/* Standard Week Editor */}
      <div className="px-4 pt-4">
        <StandardWeekEditor
          availability={recurring}
          onSaveDay={handleSaveStandardDay}
          onDeleteDay={handleDeleteStandardDay}
          onSetStandardHours={handleSetStandardHours}
          isSaving={isSaving}
        />
      </div>

      {/* Month Calendar Grid */}
      <div className="px-4 pt-4">
        <MonthCalendarGrid
          startDate={gridStartDate}
          weeksToShow={weeksToShow}
          availability={recurring}
          dateOverrides={dateSpecific}
          pto={pto}
          currentMonth={currentMonth}
          onSelectDate={handleSelectDate}
        />
      </div>

      {/* Legend */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-gray-500">Working</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <span className="text-gray-500">PTO</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-gray-500">Off</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span className="text-gray-500">Custom</span>
          </div>
        </div>
      </div>

      {/* Day Editor Bottom Sheet */}
      {selectedDate && selectedDateData && (
        <DayEditorSheet
          date={selectedDate}
          standardRule={selectedDateData.standardRule}
          currentOverride={selectedDateData.override}
          currentPto={selectedDateData.ptoDay}
          onSaveCustomHours={handleSaveCustomHours}
          onClearOverride={handleClearOverride}
          onSetPto={handleSetPto}
          onClearPto={handleClearPto}
          onClose={() => setSelectedDate(null)}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
